import { useCallback, useEffect, useState } from 'react';
import { hubAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Search, FileText, Image as ImageIcon, Film, Music, File as FileIcon,
    Download, ExternalLink, Loader2, FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const KIND_ICONS = {
    image: ImageIcon,
    video: Film,
    audio: Music,
    pdf: FileText,
    file: FileIcon,
};

const KIND_LABELS = {
    image: 'Images',
    video: 'Vidéos',
    audio: 'Audio / vocaux',
    pdf: 'PDF',
    file: 'Autres',
};

const KIND_COLORS = {
    image: 'bg-emerald-100 text-emerald-700',
    video: 'bg-purple-100 text-purple-700',
    audio: 'bg-blue-100 text-blue-700',
    pdf: 'bg-red-100 text-red-700',
    file: 'bg-slate-100 text-slate-700',
};

function fmtSize(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

function fmtDate(iso) {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
}

async function fetchAuthBlobUrl(url) {
    const token = localStorage.getItem('valnexo_token');
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    return URL.createObjectURL(await resp.blob());
}

export function HubFileSearchDialog({ open, onOpenChange }) {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [kind, setKind] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');

    // Debounce
    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 250);
        return () => clearTimeout(t);
    }, [q]);

    const search = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (debouncedQ.trim()) params.q = debouncedQ.trim();
            if (kind) params.kind = kind;
            const r = await hubAPI.searchAttachments(params);
            setResults(r.data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Recherche impossible');
        } finally {
            setLoading(false);
        }
    }, [debouncedQ, kind]);

    useEffect(() => {
        if (open) search();
    }, [open, search]);

    const download = async (r) => {
        const url = hubAPI.projectWallAttachmentUrl(r.project_id, r.message_id, r.attachment_id, 'attachment');
        try {
            const blobUrl = await fetchAuthBlobUrl(url);
            const a = document.createElement('a');
            a.href = blobUrl; a.download = r.filename || 'fichier';
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
        } catch { toast.error('Téléchargement impossible'); }
    };

    const openProject = (r) => {
        onOpenChange(false);
        navigate(`/projets/projet/${r.project_id}`);
    };

    // Grouper par projet
    const grouped = results.reduce((acc, r) => {
        if (!acc[r.project_id]) acc[r.project_id] = { name: r.project_name, items: [] };
        acc[r.project_id].items.push(r);
        return acc;
    }, {});

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="hub-file-search-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Search className="w-4 h-4" /> Rechercher un fichier dans le Hub
                    </DialogTitle>
                    <DialogDescription>
                        Retrouvez tous les documents partagés dans les murs de vos projets — images, PDF, vidéos, mémos vocaux.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Nom du fichier, extrait du message…"
                            className="pl-9"
                            data-testid="hub-file-search-input"
                        />
                    </div>
                    <div className="flex flex-wrap gap-1">
                        <button
                            onClick={() => setKind('')}
                            className={cn(
                                "text-xs rounded-full px-3 py-1.5 border transition-colors",
                                !kind ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 hover:bg-slate-50"
                            )}
                            data-testid="hub-file-filter-all"
                        >Tous</button>
                        {Object.entries(KIND_LABELS).map(([k, label]) => {
                            const Icon = KIND_ICONS[k];
                            return (
                                <button
                                    key={k}
                                    onClick={() => setKind(k === kind ? '' : k)}
                                    className={cn(
                                        "text-xs rounded-full px-3 py-1.5 border transition-colors flex items-center gap-1",
                                        kind === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 hover:bg-slate-50"
                                    )}
                                    data-testid={`hub-file-filter-${k}`}
                                >
                                    <Icon className="w-3 h-3" /> {label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Résultats */}
                <div className="flex-1 overflow-y-auto -mx-2 px-2" data-testid="hub-file-search-results">
                    {loading ? (
                        <div className="py-12 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" /> Recherche…
                        </div>
                    ) : results.length === 0 ? (
                        <div className="py-12 text-center text-slate-400 text-sm">
                            <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-30" />
                            {q || kind ? 'Aucun fichier ne correspond à votre recherche.' : 'Aucun fichier partagé pour le moment.'}
                        </div>
                    ) : (
                        <div className="space-y-4 py-2">
                            <div className="text-xs text-slate-500">{results.length} résultat{results.length > 1 ? 's' : ''}</div>
                            {Object.entries(grouped).map(([projectId, group]) => (
                                <div key={projectId} className="border border-slate-200 rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => openProject({ project_id: projectId })}
                                        className="w-full flex items-center justify-between bg-slate-50 hover:bg-slate-100 px-3 py-2 text-left"
                                        data-testid={`hub-file-project-${projectId}`}
                                    >
                                        <span className="text-sm font-semibold text-slate-700 truncate">{group.name}</span>
                                        <span className="flex items-center gap-1 text-xs text-slate-500">
                                            <Badge variant="secondary" className="text-[10px]">{group.items.length}</Badge>
                                            <ExternalLink className="w-3 h-3" />
                                        </span>
                                    </button>
                                    <div className="divide-y divide-slate-100">
                                        {group.items.map((r) => {
                                            const Icon = KIND_ICONS[r.kind] || FileIcon;
                                            return (
                                                <div key={r.attachment_id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50" data-testid={`hub-file-item-${r.attachment_id}`}>
                                                    <span className={cn("w-8 h-8 rounded-md flex items-center justify-center shrink-0", KIND_COLORS[r.kind] || KIND_COLORS.file)}>
                                                        <Icon className="w-4 h-4" />
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm text-slate-800 truncate">{r.filename}</div>
                                                        <div className="text-[10px] text-slate-500 flex items-center gap-2">
                                                            <span>{fmtSize(r.file_size)}</span>
                                                            <span>·</span>
                                                            <span>{fmtDate(r.uploaded_at)}</span>
                                                            {r.message_content && (<>
                                                                <span>·</span>
                                                                <span className="italic truncate">« {r.message_content} »</span>
                                                            </>)}
                                                        </div>
                                                    </div>
                                                    <Button size="sm" variant="ghost" onClick={() => download(r)} className="gap-1" data-testid={`hub-file-dl-${r.attachment_id}`} title="Télécharger">
                                                        <Download className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button size="sm" variant="ghost" onClick={() => openProject(r)} className="gap-1" data-testid={`hub-file-open-${r.attachment_id}`} title="Ouvrir le projet">
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default HubFileSearchDialog;
